import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { EnvelopeIcon } from "@heroicons/react/24/outline";
import {
  useGetNotificationRecipientOptionsQuery,
  useGetPlatformUserQuery,
} from "../../app/api/console/notificationsApiSlice";
import { captureException } from "../../app/sentry";

const emailKey = (email) => String(email || "").trim().toLowerCase();

const displayName = (recipient) => {
  const name = [recipient.first_name, recipient.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || recipient.email;
};

// Where Platform's preselection came from (CUS-1353). The roster default
// ("website_members") is the long-standing behavior and needs no note.
const DEFAULT_SOURCE_NOTES = {
  agent_config: "Preselected from this agent's recipient list, plus the launcher when known.",
  website_config:
    "Preselected from this website's default recipient list, plus the launcher when known.",
};
const CONFIG_SOURCES = new Set(Object.keys(DEFAULT_SOURCE_NOTES));

function RecipientGroup({ title, description, recipients, selected, onToggle }) {
  return (
    <section>
      <div className="mb-2">
        <h4 className="text-body-xs font-semibold text-text-primary">{title}</h4>
        <p className="text-caption text-text-tertiary">{description}</p>
      </div>
      {recipients.length ? (
        <div className="divide-y divide-border-subtle rounded-xl border border-border-subtle overflow-hidden">
          {recipients.map((recipient) => {
            const key = emailKey(recipient.email);
            return (
              <label
                key={key}
                className="flex items-center gap-3 px-3 py-2.5 bg-white hover:bg-surface-neutral cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selected.has(key)}
                  onChange={() => onToggle(recipient.email)}
                  className="h-4 w-4 rounded border-border-default text-accent-purple focus:ring-accent-purple/30"
                />
                <span className="min-w-0">
                  <span className="block text-body-xs font-medium text-text-primary truncate">
                    {displayName(recipient)}
                  </span>
                  {displayName(recipient) !== recipient.email && (
                    <span className="block text-caption text-text-tertiary truncate">
                      {recipient.email}
                    </span>
                  )}
                </span>
              </label>
            );
          })}
        </div>
      ) : (
        <p className="rounded-xl border border-dashed border-border-default px-3 py-3 text-caption text-text-tertiary">
          No recipients in this group.
        </p>
      )}
    </section>
  );
}

function NotificationRecipientsModal({
  isOpen,
  onClose,
  onConfirm,
  websiteId,
  useProd = true,
  // The run being notified, so Platform preselects its agent's recipient
  // config and adds the run's launcher (CUS-1353).
  agentSlug,
  runId,
  // Launcher of a staging run whose production copy has none (a run pushed
  // from staging). Platform cannot see that run, so it is added here.
  stagingLauncherUserId,
  // True while the caller is still resolving that run context.
  contextPending = false,
  // Optional operator note about that context (e.g. mixed agents).
  contextNote,
  title,
  description,
  confirmText,
  isSubmitting = false,
}) {
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [initializedFor, setInitializedFor] = useState(null);
  const initKey = [websiteId, agentSlug, runId, stagingLauncherUserId]
    .map((part) => part || "")
    .join("|");
  const { data, currentData, isFetching, isError, error } =
    useGetNotificationRecipientOptionsQuery(
      {
        websiteId,
        useProd,
        ...(agentSlug ? { agentSlug } : {}),
        ...(runId ? { runId } : {}),
      },
      { skip: !isOpen || !websiteId || contextPending }
    );
  const { currentData: stagingLauncher, isFetching: isFetchingLauncher } =
    useGetPlatformUserQuery(
      { userId: stagingLauncherUserId, useProd: false },
      { skip: !isOpen || !stagingLauncherUserId }
    );
  const contextReady = !contextPending && !isFetchingLauncher;

  // Prefer currentData so a previous website's cached payload cannot seed
  // defaults while the query arg has already moved (RTK skip/arg change race).
  // Fall back to data only when it is tagged for this website_id and nothing
  // is loading: the same website with another run's agent would otherwise
  // seed that agent's defaults.
  const options = useMemo(() => {
    const candidate = currentData ?? (isFetching ? undefined : data);
    if (!candidate || !websiteId) return null;
    if (
      candidate.website_id &&
      String(candidate.website_id) !== String(websiteId)
    ) {
      return null;
    }
    return candidate;
  }, [currentData, data, isFetching, websiteId]);

  // Platform sends a recipient config's list plus the launcher; a staging
  // launcher is ours to add. The roster default stays exactly the roster.
  // A production send rejects anyone who is neither a website member nor a
  // superuser (422 foreign_recipients), so there an off-roster launcher is
  // left out rather than failing the whole send.
  const defaultEmails = useMemo(() => {
    const defaults = options?.default_recipient_emails || [];
    const launcherEmail = stagingLauncher?.email;
    if (!launcherEmail || !CONFIG_SOURCES.has(options?.default_source)) {
      return defaults;
    }
    const key = emailKey(launcherEmail);
    if (defaults.some((email) => emailKey(email) === key)) return defaults;
    const onRoster = [
      ...(options.website_users || []),
      ...(options.superusers || []),
    ].find((recipient) => emailKey(recipient.email) === key);
    if (onRoster) return [...defaults, onRoster.email];
    return useProd ? defaults : [...defaults, launcherEmail];
  }, [options, stagingLauncher, useProd]);

  useEffect(() => {
    if (!isOpen) {
      setInitializedFor(null);
      setSelectedEmails([]);
      return;
    }
    if (!options || !contextReady) {
      // Waiting for the matching website (or the run's context) — never keep
      // a prior selection.
      setInitializedFor(null);
      setSelectedEmails([]);
      return;
    }
    if (initializedFor !== initKey) {
      setSelectedEmails(defaultEmails);
      setInitializedFor(initKey);
    }
  }, [options, contextReady, defaultEmails, initializedFor, isOpen, initKey]);

  useEffect(() => {
    if (!isError || !error) return;
    const detail =
      error?.data?.detail || error?.message || "Could not load recipients.";
    captureException(
      error instanceof Error ? error : new Error(String(detail)),
      { notification_recipients: { website_id: websiteId } }
    );
  }, [error, isError, websiteId]);

  const allRecipients = useMemo(
    () => [...(options?.website_users || []), ...(options?.superusers || [])],
    [options]
  );
  const allowedEmailKeys = useMemo(
    () => new Set(allRecipients.map((recipient) => emailKey(recipient.email))),
    [allRecipients]
  );
  // Drop any selection that is not on the current website roster (defense in
  // depth if state ever lags the options payload).
  const safeSelectedEmails = useMemo(
    () =>
      selectedEmails.filter((email) => allowedEmailKeys.has(emailKey(email))),
    [selectedEmails, allowedEmailKeys]
  );
  const selected = useMemo(
    () => new Set(safeSelectedEmails.map(emailKey)),
    [safeSelectedEmails]
  );

  const toggle = (email) => {
    const key = emailKey(email);
    setSelectedEmails((current) =>
      current.some((item) => emailKey(item) === key)
        ? current.filter((item) => emailKey(item) !== key)
        : [...current, email]
    );
  };

  const errorText =
    error?.data?.detail || error?.message || "Could not load recipients.";
  const canConfirm =
    Boolean(websiteId && options) &&
    !isFetching &&
    !isError &&
    contextReady &&
    initializedFor === initKey;
  const defaultSourceNote = DEFAULT_SOURCE_NOTES[options?.default_source];

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg rounded-2xl bg-white p-6 text-left shadow-xl">
                <div className="flex items-start gap-3">
                  <div className="rounded-xl bg-accent-purple/10 p-2">
                    <EnvelopeIcon className="h-5 w-5 text-accent-purple" />
                  </div>
                  <div>
                    <Dialog.Title className="text-body-lg font-semibold text-text-primary">
                      {title}
                    </Dialog.Title>
                    <p className="mt-1 text-body-xs text-text-secondary">
                      {description}
                    </p>
                    {websiteId && (
                      <p
                        className="mt-1 font-mono text-caption text-text-tertiary"
                        data-testid="recipient-website-id"
                      >
                        {useProd ? "Production" : "Staging"} website: {websiteId}
                      </p>
                    )}
                    {options && defaultSourceNote && (
                      <p
                        className="mt-1 text-caption text-text-tertiary"
                        data-testid="recipient-default-source"
                      >
                        {defaultSourceNote}
                      </p>
                    )}
                    {contextNote && (
                      <p
                        className="mt-1 text-caption text-text-tertiary"
                        data-testid="recipient-context-note"
                      >
                        {contextNote}
                      </p>
                    )}
                  </div>
                </div>

                <div className="mt-5 max-h-[55vh] space-y-5 overflow-y-auto pr-1">
                  {!websiteId && (
                    <p className="rounded-xl bg-severity-high/5 px-3 py-3 text-body-xs text-severity-high">
                      No {useProd ? "production" : "staging"} website is configured, so
                      recipients cannot be loaded.
                    </p>
                  )}
                  {websiteId && (isFetching || !contextReady) && (
                    <p className="text-body-xs text-text-tertiary">Loading recipients…</p>
                  )}
                  {isError && (
                    <p className="rounded-xl bg-severity-high/5 px-3 py-3 text-body-xs text-severity-high">
                      {String(errorText)}
                    </p>
                  )}
                  {options && (
                    <>
                      <RecipientGroup
                        title="Website users"
                        description={
                          defaultSourceNote
                            ? `Members of this ${
                                useProd ? "production" : "staging"
                              } website.`
                            : `Selected by default for this ${
                                useProd ? "production" : "staging"
                              } website.`
                        }
                        recipients={options.website_users || []}
                        selected={selected}
                        onToggle={toggle}
                      />
                      <RecipientGroup
                        title="Superusers"
                        description="Internal recipients; select only when needed."
                        recipients={options.superusers || []}
                        selected={selected}
                        onToggle={toggle}
                      />
                    </>
                  )}
                </div>

                {options && (
                  <div className="mt-4 flex items-center justify-between">
                    <p className="text-caption font-medium text-text-secondary">
                      {safeSelectedEmails.length} selected
                    </p>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedEmails(allRecipients.map((recipient) => recipient.email))
                        }
                        className="text-caption font-medium text-accent-purple hover:underline"
                      >
                        Select all
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedEmails([])}
                        className="text-caption font-medium text-text-secondary hover:underline"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                )}

                {options && safeSelectedEmails.length === 0 && (
                  <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-body-xs font-medium text-amber-800">
                    No email will be sent.
                  </p>
                )}

                <div className="mt-5 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-xl border border-border-default px-4 py-2 text-body-xs font-medium text-text-secondary hover:bg-surface-neutral"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={!canConfirm || isSubmitting}
                    onClick={() => onConfirm(safeSelectedEmails)}
                    className="rounded-xl bg-button-primary px-4 py-2 text-body-xs font-semibold text-white hover:bg-button-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isSubmitting ? "Working…" : confirmText}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default NotificationRecipientsModal;
